/**
 * Hero Layout Presets - Phase 1: Correct "Items Per Column" Semantics
 * 
 * CRITICAL UNDERSTANDING:
 * Layout values like [1, 3] represent ITEMS PER COLUMN, not column widths.
 * All columns have equal width. The numbers specify how many items go in each column.
 * 
 * Example: [2, 3] means:
 *   Column 1 (equal width): 2 items stacked vertically
 *   Column 2 (equal width): 3 items stacked vertically
 * 
 * This file defines preset configurations with explicit column-based item sequences.
 */

/**
 * Item Type Color Conventions
 * Used for visual representation in icons and legend
 */
export const ITEM_TYPE_COLORS = {
  image: '#10B981',           // Green - bg-green-500
  title: '#3B82F6',           // Blue - bg-blue-500
  text: '#8B5CF6',            // Purple - bg-purple-500
  button_collection: '#F59E0B', // Orange - bg-orange-500
  video: '#EF4444',           // Red - bg-red-500
  file: '#6B7280'             // Gray - bg-gray-500
};

/**
 * Tailwind class mappings for item types
 */
export const ITEM_TYPE_CLASSES = {
  image: 'bg-green-500',
  title: 'bg-blue-500',
  text: 'bg-purple-500',
  button_collection: 'bg-orange-500',
  video: 'bg-red-500',
  file: 'bg-gray-500'
};

/**
 * @typedef {Object} HeroLayoutPreset
 * @property {string} id - Unique identifier for the preset
 * @property {string} label - Display name
 * @property {string|null} value - JSON array string representing items per column (e.g., "[1, 3]")
 * @property {string[][]|null} columnSequences - Array of arrays, each sub-array contains item types for that column
 * @property {string} description - Human-readable description of the layout
 * @property {string} icon - Icon component name from HeroLayoutIcons
 * @property {string} category - Category for grouping (content_focused, image_gallery, balanced, custom)
 */

/**
 * Hero Layout Presets
 * Organized by number of columns and use case
 */
export const PRESETS = [
  // ========================================
  // SINGLE COLUMN LAYOUTS
  // ========================================
  
  {
    id: 'single-full-stack',
    label: 'Full Stack',
    value: '[4]',
    columnSequences: [
      ['image', 'title', 'text', 'button_collection']
    ],
    description: '4 items stacked vertically: image, title, text, and buttons',
    icon: 'SingleColumnFull',
    category: 'content_focused'
  },
  
  {
    id: 'single-simple',
    label: 'Simple Stack',
    value: '[3]',
    columnSequences: [
      ['image', 'title', 'text']
    ],
    description: '3 items stacked: image, title, and text',
    icon: 'SingleColumnSimple',
    category: 'content_focused'
  },

  {
    id: 'single-video',
    label: 'Video Stack',
    value: '[3]',
    columnSequences: [
      ['video', 'title', 'text']
    ],
    description: '3 items stacked: video, title, and text',
    icon: 'SingleColumnVideo',
    category: 'content_focused'
  },

  {
    id: 'single-file',
    label: 'File Stack',
    value: '[3]',
    columnSequences: [
      ['file', 'title', 'text']
    ],
    description: '3 items stacked: file, title, and text',
    icon: 'SingleColumnFile',
    category: 'content_focused'
  },

  {
    id: 'single-title-text',
    label: 'Title & Text',
    value: '[2]',
    columnSequences: [
      ['title', 'text']
    ],
    description: '2 items stacked: title and text only',
    icon: 'SingleColumnTitleText',
    category: 'content_focused'
  },

  // ========================================
  // TWO COLUMN LAYOUTS
  // ========================================
  
  {
    id: 'two-image-left',
    label: 'Image Left',
    value: '[1, 3]',
    columnSequences: [
      ['image'],
      ['title', 'text', 'button_collection']
    ],
    description: '1 image on left, 3 items (title/text/buttons) stacked on right',
    icon: 'TwoImageLeft',
    category: 'content_focused'
  },
  
  {
    id: 'two-image-right',
    label: 'Image Right',
    value: '[3, 1]',
    columnSequences: [
      ['title', 'text', 'button_collection'],
      ['image']
    ],
    description: '3 items (title/text/buttons) on left, 1 image stacked on right',
    icon: 'TwoImageRight',
    category: 'content_focused'
  },

  {
    id: 'two-content-image',
    label: 'Content-Image Split',
    value: '[2, 1]',
    columnSequences: [
      ['title', 'text'],
      ['image']
    ],
    description: '2 text items on left, 1 image on right',
    icon: 'TwoContentImage',
    category: 'content_focused'
  },

  {
    id: 'two-image-content',
    label: 'Image-Content Split',
    value: '[1, 2]',
    columnSequences: [
      ['image'],
      ['title', 'text']
    ],
    description: '1 image on left, 2 text items on right',
    icon: 'TwoImageContent',
    category: 'content_focused'
  },

  {
    id: 'two-image-title-text',
    label: 'Two Image Stacks',
    value: '[3, 3]',
    columnSequences: [
      ['image', 'title', 'text'],
      ['image', 'title', 'text']
    ],
    description: 'Two columns: image, title, text in each',
    icon: 'TwoImageStacks',
    category: 'balanced'
  },

  {
    id: 'two-title-text-image',
    label: 'Two Content Stacks',
    value: '[3, 3]',
    columnSequences: [
      ['title', 'text', 'image'],
      ['title', 'text', 'image']
    ],
    description: 'Two columns: title, text, image in each',
    icon: 'TwoContentStacks',
    category: 'balanced'
  },

  {
    id: 'two-title-image-text',
    label: 'Two Mixed Stacks',
    value: '[3, 3]',
    columnSequences: [
      ['title', 'image', 'text'],
      ['title', 'image', 'text']
    ],
    description: 'Two columns: title, image, text in each',
    icon: 'TwoMixedStacks',
    category: 'balanced'
  },

  {
    id: 'two-video-stacks',
    label: 'Two Video Stacks',
    value: '[3, 3]',
    columnSequences: [
      ['video', 'title', 'text'],
      ['video', 'title', 'text']
    ],
    description: 'Two columns: video, title, text in each',
    icon: 'TwoVideoStacks',
    category: 'balanced'
  },

  // ========================================
  // THREE COLUMN LAYOUTS
  // ========================================
  
  {
    id: 'three-center-focus',
    label: 'Center Focus',
    value: '[1, 2, 1]',
    columnSequences: [
      ['image'],
      ['title', 'text'],
      ['button_collection']
    ],
    description: '1 item in side columns, 2 items in center (main content)',
    icon: 'ThreeCenterFocus',
    category: 'balanced'
  },

  {
    id: 'three-image-stacks',
    label: 'Three Image Stacks',
    value: '[3, 3, 3]',
    columnSequences: [
      ['image', 'title', 'text'],
      ['image', 'title', 'text'],
      ['image', 'title', 'text']
    ],
    description: 'Three columns: image, title, text in each',
    icon: 'ThreeImageStacks',
    category: 'balanced'
  },

  {
    id: 'three-title-text-image',
    label: 'Three Content Stacks',
    value: '[3, 3, 3]',
    columnSequences: [
      ['title', 'text', 'image'],
      ['title', 'text', 'image'],
      ['title', 'text', 'image']
    ],
    description: 'Three columns: title, text, image in each',
    icon: 'ThreeContentStacks',
    category: 'balanced'
  },

  {
    id: 'three-title-image-text',
    label: 'Three Mixed Stacks',
    value: '[3, 3, 3]',
    columnSequences: [
      ['title', 'image', 'text'],
      ['title', 'image', 'text'],
      ['title', 'image', 'text']
    ],
    description: 'Three columns: title, image, text in each',
    icon: 'ThreeMixedStacks',
    category: 'balanced'
  },

  // ========================================
  // CUSTOM OPTION
  // ========================================
  
  {
    id: 'custom',
    label: 'Custom',
    value: null,
    columnSequences: null,
    description: 'Build your own custom layout',
    icon: 'CustomBuilder',
    category: 'custom'
  }
];

/**
 * Preset Categories
 * Used for organizing preset picker UI
 */
export const PRESET_CATEGORIES = {
  content_focused: [
    'single-full-stack', 
    'single-simple', 
    'single-video',
    'single-file',
    'single-title-text',
    'two-image-left', 
    'two-image-right', 
    'two-content-image', 
    'two-image-content'
  ],
  balanced: [
    'two-image-title-text',
    'two-title-text-image',
    'two-title-image-text',
    'two-video-stacks',
    'three-center-focus',
    'three-image-stacks',
    'three-title-text-image',
    'three-title-image-text'
  ],
  custom: ['custom']
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Get the total number of items needed for a preset
 * @param {HeroLayoutPreset} preset - The preset to check
 * @returns {number} - Total item count
 */
export function getPresetItemCount(preset) {
  if (!preset || !preset.columnSequences) return 0;
  return preset.columnSequences.flat().length;
}

/**
 * Get item type at a specific global position in a preset
 * @param {HeroLayoutPreset} preset - The preset to check
 * @param {number} globalIndex - The global item index (0-based)
 * @returns {{columnIndex: number, itemType: string, positionInColumn: number}|null}
 */
export function getItemTypeAtPosition(preset, globalIndex) {
  if (!preset || !preset.columnSequences) return null;
  
  let currentIndex = 0;
  for (let columnIndex = 0; columnIndex < preset.columnSequences.length; columnIndex++) {
    const column = preset.columnSequences[columnIndex];
    for (let positionInColumn = 0; positionInColumn < column.length; positionInColumn++) {
      if (currentIndex === globalIndex) {
        return {
          columnIndex,
          itemType: column[positionInColumn],
          positionInColumn
        };
      }
      currentIndex++;
    }
  }
  return null;
}

/**
 * Get all item types from a preset in order
 * @param {HeroLayoutPreset} preset - The preset
 * @returns {string[]} - Flat array of item types
 */
export function getPresetItemSequence(preset) {
  if (!preset || !preset.columnSequences) return [];
  return preset.columnSequences.flat();
}

/**
 * Validate preset consistency
 * Checks that columnSequences match the value array
 * @param {HeroLayoutPreset} preset - The preset to validate
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validatePreset(preset) {
  const errors = [];
  
  if (!preset.id) {
    errors.push('Preset missing id');
  }
  
  if (preset.value === null) {
    // Custom preset - skip validation
    return { valid: true, errors: [] };
  }
  
  if (!preset.value) {
    errors.push(`Preset ${preset.id}: missing value`);
    return { valid: false, errors };
  }
  
  if (!preset.columnSequences) {
    errors.push(`Preset ${preset.id}: missing columnSequences`);
    return { valid: false, errors };
  }
  
  try {
    const valueArray = JSON.parse(preset.value);
    
    // Check column count matches
    if (valueArray.length !== preset.columnSequences.length) {
      errors.push(
        `Preset ${preset.id}: value specifies ${valueArray.length} columns but columnSequences has ${preset.columnSequences.length} columns`
      );
    }
    
    // Check each column's item count matches
    for (let i = 0; i < valueArray.length && i < preset.columnSequences.length; i++) {
      const expectedCount = valueArray[i];
      const actualCount = preset.columnSequences[i].length;
      
      if (expectedCount !== actualCount) {
        errors.push(
          `Preset ${preset.id}: column ${i} should have ${expectedCount} items but has ${actualCount} items`
        );
      }
    }
  } catch (e) {
    errors.push(`Preset ${preset.id}: invalid value JSON - ${e.message}`);
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * Validate all presets
 * @returns {{valid: boolean, errors: Array<{preset: string, errors: string[]}>}}
 */
export function validateAllPresets() {
  const allErrors = [];
  let allValid = true;
  
  for (const preset of PRESETS) {
    const result = validatePreset(preset);
    if (!result.valid) {
      allValid = false;
      allErrors.push({
        preset: preset.id,
        errors: result.errors
      });
    }
  }
  
  return {
    valid: allValid,
    errors: allErrors
  };
}

/**
 * Find a preset by its value (layout array)
 * @param {string} layoutValue - JSON array string like "[1, 3]"
 * @returns {HeroLayoutPreset|null} - Matching preset or null
 */
export function findPresetByValue(layoutValue) {
  if (!layoutValue) return null;
  
  // Normalize the value for comparison (remove whitespace)
  const normalized = layoutValue.replace(/\s/g, '');
  
  return PRESETS.find(preset => {
    if (!preset.value) return false;
    const presetNormalized = preset.value.replace(/\s/g, '');
    return presetNormalized === normalized;
  }) || null;
}

/**
 * Find a preset by its ID
 * @param {string} presetId - The preset ID
 * @returns {HeroLayoutPreset|null}
 */
export function findPresetById(presetId) {
  return PRESETS.find(preset => preset.id === presetId) || null;
}

/**
 * Get default preset (single column full stack)
 * @returns {HeroLayoutPreset}
 */
export function getDefaultPreset() {
  return PRESETS[0]; // single-full-stack
}

/**
 * Get presets by category
 * @param {string} category - Category name
 * @returns {HeroLayoutPreset[]}
 */
export function getPresetsByCategory(category) {
  const categoryIds = PRESET_CATEGORIES[category] || [];
  return PRESETS.filter(preset => categoryIds.includes(preset.id));
}

/**
 * Get the column distribution for a preset
 * Returns array showing how many items are in each column
 * @param {HeroLayoutPreset} preset
 * @returns {number[]|null}
 */
export function getColumnDistribution(preset) {
  if (!preset || !preset.value) return null;
  try {
    return JSON.parse(preset.value);
  } catch (e) {
    return null;
  }
}

// Run validation in development
if (process.env.NODE_ENV === 'development') {
  const validation = validateAllPresets();
  if (!validation.valid) {
    console.error('❌ Hero Layout Preset Validation Errors:');
    validation.errors.forEach(({ preset, errors }) => {
      console.error(`  Preset "${preset}":`);
      errors.forEach(err => console.error(`    - ${err}`));
    });
  } else {
    console.log('✅ All hero layout presets validated successfully');
  }
}

export default PRESETS;
